import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { translate } from '../../Services/translation';
import { add as addToCart } from '../../Services/Cart.service';
import {
  receive as receiveCart,
  loadError as onCartError,
  triggerAddToCartAnimation,
} from '../../Actions/Cart.action';
import { catchError } from '../../Actions/Error.action';
import LoadingSpinner from '../LoadingSpinner';
import Icon from '../Icon';
import clsx from 'clsx';

interface BuyButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  articleNumber?: string;
  quantity?: number;
  disabled?: boolean;
  label?: any;
  styleClassName?: string;
  hideIcon?: boolean;
  btnSize?: 'large' | 'medium';
  persistCheckmark?: boolean;
  onAddToCart?: (articleNumber: string, quantity: number) => void;
}

const opacityTransitionClasses = 'transition-opacity duration-150';
const stateIconClasses = clsx(
  'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
  opacityTransitionClasses
);

const BuyButton = ({
  articleNumber,
  quantity = 1,
  label,
  disabled,
  className,
  styleClassName = 'btn--buy-button',
  onClick = undefined,
  hideIcon = false,
  btnSize = 'large',
  persistCheckmark,
  onAddToCart,
  ...buttonProps
}: BuyButtonProps) => {
  const dispatch = useDispatch();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [showCheckmark, setShowCheckmark] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [checkmarkTimeoutId, setCheckmarkTimeoutId] = useState<
    NodeJS.Timeout | undefined
  >();

  const onButtonClick = async (
    ev: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setIsLoading(true);
    const loadingSpinnerTimeout = setTimeout(
      () => setShowLoadingSpinner(true),
      200
    );

    try {
      if (onClick) {
        // await in case the method returns a promise
        await onClick(ev);
      } else if (!!articleNumber) {
        const cart = await addToCart({
          articleNumber,
          quantity,
        });
        dispatch(receiveCart(cart));
        dispatch(triggerAddToCartAnimation());
        onAddToCart && onAddToCart(articleNumber, quantity);
      }
      onAddToCartSuccess();
    } catch (err) {
      dispatch(catchError(err, (error) => onCartError(error)));
    } finally {
      setIsLoading(false);
      clearTimeout(loadingSpinnerTimeout);
      setShowLoadingSpinner(false);
    }
  };

  const onAddToCartSuccess = () => {
    setShowCheckmark(true);
    if (!persistCheckmark) {
      setCheckmarkTimeoutId(
        setTimeout(() => setShowCheckmark(false), 2500)
      );
    }
  };

  useEffect(() => {
    if (typeof persistCheckmark === 'undefined') return;

    if (!persistCheckmark) {
      setShowCheckmark(false);
    } else {
      clearTimeout(checkmarkTimeoutId);
    }
  }, [persistCheckmark, checkmarkTimeoutId]);

  return (
    <button
      className={clsx(
        className,
        'btn relative w-full',
        btnSize === 'medium' ? 'btn--medium' : 'btn--large',
        styleClassName,
        (isLoading || showCheckmark || showLoadingSpinner) &&
          'disabled:opacity-80'
      )}
      disabled={disabled || isLoading || showCheckmark}
      {...buttonProps}
      onClick={onButtonClick}
    >
      <span
        className={clsx(
          showLoadingSpinner || showCheckmark
            ? 'opacity-0'
            : 'opacity-100',
          opacityTransitionClasses,
          'flex items-center justify-center'
        )}
      >
        {label ?? (
          <>
            {!hideIcon && (
              <Icon name="shopping-cart" className="mr-3 h-6 w-6" />
            )}
            {translate('general.add-to-cart')}{' '}
          </>
        )}
      </span>
      <span
        className={clsx(
          showLoadingSpinner && !showCheckmark
            ? 'opacity-100'
            : 'opacity-0',
          stateIconClasses
        )}
      >
        <LoadingSpinner />
      </span>
      <Icon
        name="check"
        className={clsx(
          showCheckmark ? 'opacity-100' : 'opacity-0',
          stateIconClasses
        )}
      />
    </button>
  );
};

export default BuyButton;
